import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FOOTBALL_MARKET_INSTANT_BUY } from '@sorare/core/src/constants/__generated__/routes';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  goToProUnlockProModeMutation,
  goToProUnlockProModeMutationVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit) var(--triple-unit) var(--double-unit);
`;
const CautionParagraph = styled(BodyS).attrs({
  as: 'p',
  color: 'var(--c-nd-700)',
})`
  align-self: flex-start;
`;
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  align-items: center;
  & > * {
    width: 100%;
  }
  @media ${laptopAndAbove} {
    flex-direction: row;
    & > * {
      flex: 1;
    }
  }
`;

const UNLOCK_PRO_MODE_MUTATION = gql`
  mutation goToProUnlockProModeMutation($input: unlockProModeInput!) {
    unlockProMode(input: $input) {
      currentUser {
        slug
        forcedIntoOnboarding
        anyUserSportProfile(sport: FOOTBALL) {
          id
          ... on FootballUserSportProfile {
            id
            proUnlocked
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  goToProUnlockProModeMutation,
  goToProUnlockProModeMutationVariables
>;

type Props = {
  onClose: () => void;
  open: boolean;
};
export const GoToProWarningDialog = ({ onClose, open }: Props) => {
  const [mutate, { loading }] = useMutation(UNLOCK_PRO_MODE_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const getInteractionContext = useGetInteractionContext();
  const navigate = useNavigate();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      hideHeader
      fullScreen={false}
      footer={
        <ButtonsWrapper>
          <Button size="medium" color="quaternary" onClick={onClose}>
            <FormattedMessage
              id="GoToProWarningDialog.cancel"
              defaultMessage="Stay in Beginner"
            />
          </Button>
          <LoadingButton
            size="medium"
            color="primary"
            loading={loading}
            onClick={async () => {
              await mutate({
                variables: {
                  input: {
                    sport: Sport.FOOTBALL,
                    rarity: Rarity.limited,
                    interactionContext: getInteractionContext(),
                  },
                },
              });
              navigate(FOOTBALL_MARKET_INSTANT_BUY, {
                replace: true,
              });
            }}
          >
            <FormattedMessage
              id="GoToProWarningDialog.continue"
              defaultMessage="Yes, Let's Go!"
            />
          </LoadingButton>
        </ButtonsWrapper>
      }
    >
      <Root>
        <FontAwesomeIcon icon={faTriangleExclamation} fontSize="24px" />
        <Vertical center>
          <HeadlineL>
            <FormattedMessage
              id="GoToProWarningDialog.title"
              defaultMessage="Are you ready?"
            />
          </HeadlineL>
        </Vertical>
        <CautionParagraph>
          <FormattedMessage
            id="GoToProWarningDialog.warningParagraph"
            defaultMessage="You’ve mastered the basics. The world of Pro awaits with new challenges and amazing rewards. Ready to unlock the full Sorare experience?"
          />
        </CautionParagraph>
      </Root>
    </Dialog>
  );
};
